import Key from '@o2/icons/Key';
import { ComponentPropsWithoutRef, ElementType, MouseEventHandler, ReactNode } from 'react';
import { styled, useMediaQuery, useTheme } from '@mui/material';
import colors from '@o2/ui/theme/colors';
import Tooltip, { TooltipProps } from './Tooltip';
import palette from '@o2/ui/theme/palette';

const StyledButton = styled('button', {
  name: 'AppBar',
  slot: 'LoginButton',
})(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  background: 'none',
  color: palette.primary.main,
  border: 'none',
  cursor: 'pointer',
  textDecoration: 'none',
  padding: theme.spacing(2, 2.5),
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  fontSize: 14,
  fontWeight: 500,
  transition: '500ms background-color',
  whiteSpace: 'nowrap',
  gap: theme.spacing(2),
  '&:hover': {
    textDecoration: 'underline',
    textUnderlineOffset: '0.3em',
  },
  '&:last-child': {
    marginRight: theme.spacing(-2.5),
    [theme.breakpoints.only('xs')]: {
      marginRight: theme.spacing(-0.75),
    },
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1.5),
  },
}));

export type LoginButtonProps<T extends ElementType = 'button'> = {
  children: ReactNode;
  icon?: ReactNode;
  onClick?: MouseEventHandler;

  /**
   * Tooltip content.
   *
   * Not working on touch screens.
   */
  tooltip?: ReactNode;

  /**
   * Props passed to the Tooltip component.
   */
  tooltipProps?: Omit<TooltipProps, 'title' | 'children' | 'ref'>;
  as?: T;
} & ComponentPropsWithoutRef<T>;

const LoginButton = <T extends ElementType = 'button'>({
  tooltip,
  icon,
  children,
  tooltipProps,
  ...other
}: LoginButtonProps<T>) => {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const btn = (
    <StyledButton {...other}>
      {smallScreen && <>{icon ? icon : <Key sx={{ fontSize: smallScreen ? 28 : 32 }} />}</>}
      {!smallScreen && children}
    </StyledButton>
  );

  return tooltip ? (
    <Tooltip title={tooltip} {...tooltipProps}>
      {btn}
    </Tooltip>
  ) : (
    btn
  );
};

LoginButton.displayName = 'AppBar.LoginButton';

export default LoginButton;
